import { Zap, Shovel, Bug, ShieldAlert, Eye, UserCheck } from "lucide-react";
import { Section, Bullets, SubTitle } from "@/components/analysis/resultUi";
import { Items, Note, mgmtOf, MgmtBlock, BioItem, Substance, VerifiedUse, Signs } from "@/components/analysis/ConsultParts";

function Immediate({ c }) {
  const d = c.do_now || {};
  return (
    <Section icon={Zap} title="Cosa fare subito" testid="consult-immediate">
      {c.practice_issues?.length > 0 && (
        <><SubTitle>Da correggere nella gestione attuale</SubTitle>
          <Items items={c.practice_issues.map((p) => ({ what: p.practice, why: p.problem, how: p.correction }))} prefix="consult-practice" /></>
      )}
      {d.immediate?.length > 0 && <><SubTitle>Subito</SubTitle><Items items={d.immediate} prefix="consult-immediate-item" /></>}
      {d.following?.length > 0 && <><SubTitle>Nei giorni e nelle settimane successive</SubTitle><Items items={d.following} prefix="consult-following-item" /></>}
      {d.avoid?.length > 0 && <><SubTitle>Cosa evitare</SubTitle><Bullets items={d.avoid} /></>}
    </Section>
  );
}

function Agronomic({ c }) {
  const tech = c.phyto?.technical_means || [];
  return (
    <Section icon={Shovel} title="Interventi agronomici e meccanici" testid="consult-agronomic">
      <MgmtBlock m={mgmtOf(c, "agronomico")} title="Operazioni colturali" prefix="consult-agro" />
      <MgmtBlock m={mgmtOf(c, "fisico_meccanico")} title="Interventi manuali e meccanici" prefix="consult-mech" />
      {tech.length > 0 && <><SubTitle>Mezzi tecnici (non fitosanitari)</SubTitle><Items items={tech} prefix="consult-tech" /></>}
    </Section>
  );
}

function Biological({ c }) {
  const m = mgmtOf(c, "biologico");
  return (
    <Section icon={Bug} title="Controllo biologico" testid="consult-biological">
      {m.applicable ? <div className="space-y-3">{m.items.map((it, i) => <BioItem key={i} it={it} i={i} />)}</div> : <Note testid="consult-bio-na">{m.note}</Note>}
    </Section>
  );
}

function Phyto({ c }) {
  const p = c.phyto || {};
  return (
    <Section icon={ShieldAlert} title="Prodotti fitosanitari" testid="consult-phyto">
      {p.statement && <p className="text-[17px] leading-relaxed text-[#2D3A30]" data-testid="consult-phyto-statement">{p.statement}</p>}
      {p.substances?.length > 0 && <><SubTitle>Sostanze attive pertinenti alla strategia di controllo</SubTitle><div className="space-y-3">{p.substances.map((s, i) => <Substance key={s.iso} s={s} i={i} />)}</div></>}
      {p.verified_uses?.length > 0 && <><SubTitle>Impieghi verificati per agrumi e questa avversità</SubTitle><div className="space-y-3">{p.verified_uses.map((u, i) => <VerifiedUse key={u.id || i} u={u} i={i} />)}</div></>}
      {p.excluded_substances?.length > 0 && <><SubTitle>Sostanze escluse</SubTitle><Bullets items={p.excluded_substances.map((x) => `${x.name}: ${x.reason}`)} /></>}
      {p.strategy_notice && <p className="mt-4 rounded-xl bg-[#f8f3e9] p-3 text-[16px] leading-relaxed text-[#2D3A30]" data-testid="consult-strategy-notice">{p.strategy_notice}</p>}
      {p.label_notice && <p className="mt-3 rounded-xl bg-[#f8f3e9] p-3 text-[16px] leading-relaxed text-[#2D3A30]" data-testid="consult-label-notice">{p.label_notice}</p>}
    </Section>
  );
}

function Monitoring({ c }) {
  const d = c.do_now || {};
  const prev = mgmtOf(c, "prevenzione");
  return (
    <Section icon={Eye} title="Monitoraggio e prevenzione" testid="consult-monitoring">
      {d.monitoring?.length > 0 && <><SubTitle>Cosa controllare nei prossimi giorni</SubTitle><Items items={d.monitoring} prefix="consult-monitor" /></>}
      <Signs title="Segnali che gli interventi funzionano" items={d.improvement_signs} />
      <Signs title="Segnali di peggioramento" items={d.worsening_signs} />
      {d.reassess && <><SubTitle>Quando rivalutare</SubTitle><p className="text-[17px] leading-relaxed text-[#2D3A30]">{d.reassess}</p></>}
      <MgmtBlock m={prev} title="Prevenzione" prefix="consult-prev" />
      <Signs title="Condizioni che favoriscono il problema" items={prev.favoring_conditions} />
      {prev.intervene_when && <p className="mt-3 text-[17px] leading-relaxed text-[#2D3A30]"><strong>Quando intervenire:</strong> {prev.intervene_when}</p>}
    </Section>
  );
}

function Professional({ c }) {
  const d = c.do_now || {};
  if (!d.escalate && !d.professional && !c.safety?.length) return null;
  return (
    <Section icon={UserCheck} title="Quando chiedere un professionista" testid="consult-professional">
      {d.professional && <p className="text-[17px] leading-relaxed text-[#2D3A30]">{d.professional}</p>}
      {d.escalate && <p className="mt-2 text-[17px] leading-relaxed text-[#2D3A30]"><strong>Quando passare a un intervento successivo:</strong> {d.escalate}</p>}
      <Signs title="Sicurezza e controllo successivo" items={c.safety} />
    </Section>
  );
}

export default function ResultConsult({ consult: c }) {
  return (
    <div className="space-y-4" data-testid="result-consult">
      <Immediate c={c} />
      <Agronomic c={c} />
      <Biological c={c} />
      <Phyto c={c} />
      <Monitoring c={c} />
      <Professional c={c} />
    </div>
  );
}
